import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import MultiAttachment, {
  type InitialFilesMetadata,
} from '~/components/common/inputs/attachment/attachment-dialog/multi-attachment/MultiAttachment'
import MultiAttachmentDialog, {
  type MultiAttachmentDialogProps,
} from '~/components/common/inputs/attachment/attachment-dialog/multi-attachment/MultiAttachmentDialog'
import { fetchDashboardDetails } from '~/store/duck/imagingDashboard'
import type { AttachmentGroup, FilesWithDetails } from '~/types'

import { useCreateManualAttachmentFiles } from './useCreateManualAttachmentFiles'

const useInitialFiles = (initialFilesMetadata?: InitialFilesMetadata[]) => {
  const [files, setFiles] = useState<File[]>()

  useEffect(() => {
    const fetchAndCreateFiles = async () => {
      if (!initialFilesMetadata) return

      const filesPromises = initialFilesMetadata.map(async (fileData) => {
        const response = await fetch(fileData.url)
        const blob = await response.blob()
        return new File([blob], fileData.fileName, {
          type: fileData.fileType,
        })
      })

      const loadedFiles = await Promise.all(filesPromises)
      setFiles(loadedFiles)
    }

    fetchAndCreateFiles()
  }, [initialFilesMetadata])
  return files
}

export interface ImagingAttachmentDialogProps
  extends Omit<MultiAttachmentDialogProps, 'renderMultiAttachment'> {
  businessId: string
  clientId: string
  imagingDashboardRecordId: string
  initialFilesMetadata: InitialFilesMetadata[]
  initialTitle?: string
  maxLimit?: number
  onAttachmentsUploaded?: () => void
  orderId: string
  patientId: string
  preexistingFileCount: number
}

const ImagingAttachmentDialog = ({
  onClose,
  open,
  initialFilesMetadata,
  initialTitle,
  maxLimit,
  orderId,
  businessId,
  clientId,
  patientId,
  imagingDashboardRecordId,
  preexistingFileCount,
  onAttachmentsUploaded,
}: ImagingAttachmentDialogProps) => {
  const dispatch = useDispatch()
  const { createAttachments, loading } = useCreateManualAttachmentFiles(
    businessId,
    clientId,
    patientId,
    orderId,
  )

  const onFilesUpload = async (group: AttachmentGroup) => {
    try {
      const filesWithDetails = group as FilesWithDetails
      await createAttachments(filesWithDetails.files)

      // Refetch imaging dashboard details to update attachment count
      dispatch(fetchDashboardDetails(imagingDashboardRecordId))

      // Refetch attachments if callback provided
      if (onAttachmentsUploaded) {
        onAttachmentsUploaded()
      }

      // Close dialog after successful upload
      if (onClose) {
        onClose()
      }
    } catch {
      // Error is handled by the mutation hook
    }
  }

  const initialFiles = useInitialFiles(initialFilesMetadata)

  const areInitialFilesPending = initialFilesMetadata && !initialFiles

  if (areInitialFilesPending) {
    return null
  }

  return (
    <MultiAttachmentDialog
      open={open}
      renderMultiAttachment={({
        closeEdit,
        fileIndexForImageEdit,
        setFileIndexForImageEdit,
      }) => (
        <MultiAttachment
          closeEdit={closeEdit}
          fileIndexForImageEdit={fileIndexForImageEdit}
          initialFiles={initialFiles}
          initialTitle={initialTitle}
          isSaving={loading}
          maxLimit={maxLimit}
          preexistingFileCount={preexistingFileCount}
          setFileIndexForImageEdit={setFileIndexForImageEdit}
          showAttachmentSource={false}
          onFilesUpload={onFilesUpload}
        />
      )}
      onClose={onClose}
    />
  )
}

export default ImagingAttachmentDialog
